/**
 * Feedback API Types and Interfaces for Public Websearch Chatbot Client
 *
 * Defines TypeScript types for the feedback system including API contracts,
 * UI state management, and complete feedback flow state machine.
 */

/**
 * Feedback result enum values matching backend FeedbackResultEnum
 * These are the actual values used in API communication
 */
export const FEEDBACK_RESULT_VALUES = {
  SOLVED: 1,
  NOT_HELPFUL: 2,
} as const;

/**
 * Feedback type enumeration - uses API enum values directly
 */
export type FeedbackType =
  (typeof FEEDBACK_RESULT_VALUES)[keyof typeof FEEDBACK_RESULT_VALUES];

/**
 * Message role constants
 * Used for filtering and identifying different types of chat messages
 */
export const MESSAGE_ROLES = {
  /** Messages from the user */
  USER: 'user',
  /** Messages from the AI assistant */
  ASSISTANT: 'assistant',
} as const;

/**
 * Content type constants for message content
 * Used in @assistant-ui/react message content structures
 */
export const CONTENT_TYPES = {
  /** Text content in chat messages */
  TEXT: 'text',
  /** Image content in chat messages (for future use) */
  IMAGE: 'image',
} as const;

/**
 * Feedback request payload sent to the API
 * POST /apps/public-websearch-chatbot-client/api/v1/feedback
 */
export interface FeedbackRequest {
  /** Current session ID from SessionManager */
  chatId: string;

  /** Follow-up comment/message from user */
  message: string;

  /** Feedback result using FeedbackResultEnum (1=Solved, 2=NotHelpful) */
  result: number;
}

/**
 * Feedback response payload from the API
 * Follows existing API response pattern with service/payload structure
 */
export interface FeedbackResponse {
  /** Service metadata with error handling */
  service: {
    /** Error code - 0 for success, non-zero for errors */
    errorCode: number;

    /** Human-readable error message if errorCode !== 0 */
    errorMessage?: string | null;
  };

  /** Response payload data - null for feedback endpoint */
  payload: null;
}

/**
 * Props interface for Feedback component
 */
export interface FeedbackProps {
  /** Message ID to associate feedback with */
  messageId: string;

  /** Whether to disable the feedback buttons */
  disabled?: boolean;
}

/**
 * Props interface for individual FeedbackButton component
 */
export interface FeedbackButtonProps {
  /** Type of feedback this button represents */
  type: FeedbackType;

  /** Button text/label */
  children: React.ReactNode;

  /** Whether this button is currently selected */
  isSelected: boolean;

  /** Whether this button is disabled */
  isDisabled: boolean;

  /** Whether this button is in loading state */
  isLoading: boolean;

  /** Click handler */
  onClick: () => void;

  /** Optional custom styling */
  className?: string;
}

/**
 * Tailwind class names for feedback button styling
 * Uses semantic colors from design system - indexed by enum values
 */
export const FEEDBACK_CLASSES = {
  /** Solved feedback styling (green) - value 1 */
  [FEEDBACK_RESULT_VALUES.SOLVED]: {
    border: 'border-green-700',
    text: 'text-green-700',
    icon: 'text-green-700',
    background: 'bg-gray-100',
    hoverBorder: 'hover:border-green-700',
  },

  /** NotHelpful feedback styling (red) - value 2 */
  [FEEDBACK_RESULT_VALUES.NOT_HELPFUL]: {
    border: 'border-destructive',
    text: 'text-destructive',
    icon: 'text-destructive',
    background: 'bg-gray-100',
    hoverBorder: 'hover:border-destructive',
  },

  /** Default/neutral styling (gray) */
  default: {
    border: 'border-gray-300',
    text: 'text-neutral-500',
    icon: 'text-gray-500',
    background: 'bg-gray-100',
  },
} as const;

/**
 * Message metadata interface for feedback state persistence
 * Extends the existing @assistant-ui/react message metadata structure
 */
export interface FeedbackMessageMetadata {
  /** Session ID (already exists) */
  sessionId: string;

  /** Feedback state for this message */
  feedbackState?: {
    /** Selected feedback type */
    selected: FeedbackType;

    /** Whether feedback process is completed */
    completed: boolean;

    /** Reference to original assistant message ID */
    messageId: string;

    /** User's submitted feedback text (optional, stored after form submission) */
    feedbackText?: string;

    /** Timestamp when feedback was submitted (optional) */
    submittedAt?: number;
  };
}

/**
 * Extended message metadata that includes feedback state
 * This matches the existing pattern used in the codebase
 */
export interface MessageWithFeedbackMetadata {
  metadata: {
    custom: FeedbackMessageMetadata;
  };
  role: typeof MESSAGE_ROLES.ASSISTANT | typeof MESSAGE_ROLES.USER;
  isLast?: boolean;
  id: string;
}

/**
 * Text constants for feedback buttons and inline feedback UI
 */
export const FEEDBACK_TEXT = {
  /** Solved feedback text - value 1 */
  [FEEDBACK_RESULT_VALUES.SOLVED]: 'That solved it!',
  /** NotHelpful feedback text - value 2 */
  [FEEDBACK_RESULT_VALUES.NOT_HELPFUL]: "This didn't help",
  /** Prompt shown in feedback form */
  formPrompt:
    'Thank you! Feel free to let me know if you have any details to share.',
  /** Privacy warning shown in feedback form */
  privacyWarning:
    'Please do not share any personal, confidential or sensitive information',
  /** Confirmation message shown after feedback submission */
  confirmationMessage: 'Thank you for your feedback!',
  /** Header for feedback display */
  feedbackHeader: 'Feedback',
  /** Submit button text */
  submitButton: 'Submit',
} as const;
